import { z } from 'zod';

import { UserGroupNames } from '../Permissions';

export enum ResetType {
  PASSWORD = 'PASSWORD',
  ONBOARDING = 'ONBOARDING',
  OTP = 'OTP',
}

export const forgotPasswordSchema = z.object({
  email: z.string().email('Please enter a valid email.'),
});

export type ForgotPasswordSchema = z.infer<typeof forgotPasswordSchema>;

export const changePasswordInputSchema = z
  .object({
    new_password: z.string().min(8, 'Passwords must be a minimum of 8 characters'),
    confirm_password: z.string().min(8, 'Passwords must be a minimum of 8 characters'),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    message: 'Passwords do not match',
    path: ['confirm_password'],
  });

export type ChangePasswordInputSchema = z.infer<typeof changePasswordInputSchema>;

export const resetTokenSchema = z.object({
  token: z.string().min(1),
  reset_type: z.enum(Object.values(ResetType) as [string, ...string[]]),
});

export type ResetTokenSchema = z.infer<typeof resetTokenSchema>;

export const changePasswordSchema = resetTokenSchema.extend({
  new_password: z.string().min(1),
});

export type ChangePasswordSchema = z.infer<typeof changePasswordSchema>;

export const userManagementSchema = z
  .object({
    id: z.number().optional(),
    email: z.string().min(1, 'Email is required').email('Please enter a valid email.'),
    first_name: z.string().min(1, 'First name is required').max(150, 'First name must be fewer than 150 characters'),
    last_name: z.string().min(1, 'Last name is required').max(150, 'Last name must be fewer than 150 characters'),
    is_active: z.boolean().optional(),
    user_type: z.enum(Object.values(UserGroupNames) as [string, ...string[]]),
    client_associations: z.array(z.string()).optional(),
    project_associations: z.array(z.string()).optional(),
    farm_associations: z.array(z.string()).optional(),
    phone_number: z.string().optional(),
    country_code: z.string().length(2, 'Use 2-letter ISO code').optional(),
  })
  .refine(
    (data) => {
      if (data.user_type === UserGroupNames.CLIENT_ADMIN) {
        return (data.client_associations?.length ?? 0) > 0;
      }
      return true;
    },
    {
      message: 'You must pick a customer',
      path: ['client_associations'],
    }
  )
  .refine(
    (data) => {
      if (data.user_type === UserGroupNames.PROJECT_MANAGER || data.user_type === UserGroupNames.AGRI_PM) {
        return (data.project_associations?.length ?? 0) > 0;
      }
      return true;
    },
    {
      message: 'You must pick at least one project',
      path: ['project_associations'],
    }
  )
  .refine(
    (data) => {
      if (data.user_type === UserGroupNames.FARMER) {
        return (data.farm_associations?.length ?? 0) > 0;
      }
      return true;
    },
    {
      message: 'You must pick at least one farm',
      path: ['farm_associations'],
    }
  );

export type UserManagementSchema = z.infer<typeof userManagementSchema>;

export interface UserDeactivateSchema {
  is_active: boolean;
}

export const otpSetupInputSchema = z.object({
  phone_number: z.string(),
  country_code: z.string().length(2),
});

export type OtpSetupInputSchema = z.infer<typeof otpSetupInputSchema>;

export const otpSetupSchema = resetTokenSchema.merge(otpSetupInputSchema);

export type OtpSetupSchema = z.infer<typeof otpSetupSchema>;

export const generateResetTokenInputSchema = z.object({
  reset_type: z.string(),
  password: z.string(),
});

export type GenerateResetTokenInputSchema = z.infer<typeof generateResetTokenInputSchema>;

export const changePasswordFormInputSchema = z
  .object({
    oldPassword: z.string().min(1, 'Please enter your current password'),
    newPassword: z.string().min(8, 'Password must be a minimum of 8 characters'),
    confirmNewPassword: z.string().min(8, 'Password must be a minimum of 8 characters'),
  })
  .refine((data) => data.newPassword === data.confirmNewPassword, {
    message: 'Passwords do not match',
    path: ['confirmNewPassword'],
  });

export type ChangePasswordFormInputSchema = z.infer<typeof changePasswordFormInputSchema>;
